// Workspace intelligence store — the compounding layer on top of the static
// directory. Every view, enrichment, and logged outcome accrues here (keyed by
// company), and the calibration model re-derives win-rate lifts from it. The
// longer a team works in the app, the sharper its scores get.
//
// localStorage 'revailosales.intel', the same per-user store pattern the other
// client-side stores in lib/ use.

import { keyOf, attrsOf } from './companyMeta.js'
import { scopedKey } from './userScope.js'

// Per-user store (see userScope.js). Never touches the shared leads.
const STORAGE_BASE = 'revailosales.intel'
const storageKey = () => scopedKey(STORAGE_BASE)
const LOG_LIMIT = 2000

// A row snapshot without the transient annotations (_score/_intel/_delta) so a
// tracked company can be re-opened (priority queue, deep link) without re-fetching.
function snapshotOf(company) {
  const out = {}
  for (const [k, v] of Object.entries(company)) {
    if (!k.startsWith('_')) out[k] = v
  }
  return out
}

export const OUTCOME_TYPES = [
  { value: 'replied', label: 'Replied' },
  { value: 'meeting', label: 'Meeting booked' },
  { value: 'won', label: 'Won' },
  { value: 'lost', label: 'Lost' },
]

const STAGE_RANK = { viewed: 0, enriched: 1, replied: 2, meeting: 3, won: 4, lost: 4 }

function read() {
  try {
    const raw = JSON.parse(localStorage.getItem(storageKey()))
    if (raw && typeof raw === 'object') {
      return { companies: raw.companies || {}, log: raw.log || [] }
    }
  } catch {
    // fall through to a fresh store
  }
  return { companies: {}, log: [] }
}

function write(store) {
  if (store.log.length > LOG_LIMIT) store.log = store.log.slice(-LOG_LIMIT)
  localStorage.setItem(storageKey(), JSON.stringify(store))
}

function ensure(store, company, category) {
  const id = keyOf(company)
  if (!id) return null
  let rec = store.companies[id]
  if (!rec) {
    rec = {
      id,
      name: company.name || String(id),
      category: category || company._category || '',
      region: company._region || '',
      firstSeenAt: new Date().toISOString(),
      views: 0,
      lastViewedAt: null,
      enriched: false,
      enrichedAt: null,
      outcomes: [],
      notes: [],
      stage: 'viewed',
      attrs: attrsOf(company),
      company: snapshotOf(company),
    }
    store.companies[id] = rec
  } else {
    // Refresh the attribute + row snapshot when we see richer data (e.g. post-enrich).
    rec.attrs = attrsOf(company)
    rec.company = snapshotOf(company)
    if (category) rec.category = category
  }
  return rec
}

function bump(store, kind) {
  store.log.push({ at: new Date().toISOString(), kind })
}

export function recordView(company, category) {
  const store = read()
  const rec = ensure(store, company, category)
  if (!rec) return
  rec.views += 1
  rec.lastViewedAt = new Date().toISOString()
  bump(store, 'view')
  write(store)
}

export function recordEnrich(companies, category) {
  const store = read()
  for (const company of companies) {
    const rec = ensure(store, company, category)
    if (!rec) continue
    rec.enriched = true
    rec.enrichedAt = new Date().toISOString()
    if (STAGE_RANK[rec.stage] < STAGE_RANK.enriched) rec.stage = 'enriched'
    bump(store, 'enrich')
  }
  write(store)
}

export function recordOutcome(company, category, type) {
  if (!OUTCOME_TYPES.some((o) => o.value === type)) return
  const store = read()
  const rec = ensure(store, company, category)
  if (!rec) return
  rec.outcomes.push({ type, at: new Date().toISOString() })
  if (STAGE_RANK[type] >= STAGE_RANK[rec.stage]) rec.stage = type
  bump(store, 'outcome')
  write(store)
}

// Move a lead to a stage directly — what dragging a card on the Deals board
// does. Unlike recordOutcome() this can move a deal BACKWARDS (a "won" dragged
// back to "replied" after a deal falls through), so it sets the stage outright
// instead of taking the max rank. It still appends an outcome row when the
// stage is one of the logged outcome types, so the funnel and the calibration
// see the same history the outcome buttons produce.
export function setStage(company, category, stage) {
  const store = read()
  const rec = ensure(store, company, category)
  if (!rec || rec.stage === stage) return null
  const isOutcome = OUTCOME_TYPES.some((o) => o.value === stage)
  if (isOutcome) rec.outcomes.push({ type: stage, at: new Date().toISOString() })
  rec.stage = stage
  bump(store, 'outcome')
  write(store)
  return rec
}

// ---------- Notes ----------
// Free-form working notes on a lead. A lead holds any number of them; they live
// on the intel record so they survive deep links and travel with the snapshot.

const noteId = () => `n_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`

export function addNote(company, category, text, author = {}) {
  const body = String(text || '').trim()
  if (!body) return null
  const store = read()
  const rec = ensure(store, company, category)
  if (!rec) return null
  const note = {
    id: noteId(),
    text: body,
    at: new Date().toISOString(),
    editedAt: null,
    author: author.email || '',
    authorName: author.name || '',
  }
  ;(rec.notes ||= []).push(note)
  bump(store, 'note')
  write(store)
  return note
}

export function updateNote(companyOrId, id, text) {
  const body = String(text || '').trim()
  if (!body) return
  const store = read()
  const key = typeof companyOrId === 'string' ? companyOrId : keyOf(companyOrId)
  const note = store.companies[key]?.notes?.find((n) => n.id === id)
  if (!note) return
  note.text = body
  note.editedAt = new Date().toISOString()
  write(store)
}

export function deleteNote(companyOrId, id) {
  const store = read()
  const key = typeof companyOrId === 'string' ? companyOrId : keyOf(companyOrId)
  const rec = store.companies[key]
  if (!rec?.notes?.some((n) => n.id === id)) return
  rec.notes = rec.notes.filter((n) => n.id !== id)
  write(store)
}

export function getIntel(companyOrId) {
  const id = typeof companyOrId === 'string' ? companyOrId : keyOf(companyOrId)
  return read().companies[id] || null
}

export function listIntel() {
  return Object.values(read().companies)
}

// ---------- Calibration ----------
// Learns win-rate lifts from labeled outcomes. Positive labels: won / meeting.
// Negative: lost. Laplace-smoothed rates; a bucket needs >= 3 labeled examples
// before it may move a score. Confidence grows ~4 pts per labeled outcome.

const DIMENSIONS = [
  { key: 'ageBand', labels: { new: 'Under 5 yrs old', growing: '5–19 yrs old', veteran: '20+ yrs old' } },
  { key: 'sizeBand', labels: { micro: '1–9 employees', small: '10–49 employees', large: '50+ employees' } },
  { key: 'tradeBand', labels: { specialist: 'Specialists', multi: 'Multi-line', turnkey: 'Full-service firms' } },
  { key: 'email', labels: { true: 'Has email', false: 'No email' } },
  { key: 'volume', labels: { true: 'Volume disclosed', false: 'Volume undisclosed' } },
]

const MIN_BUCKET = 3

export function calibration() {
  const companies = listIntel()
  const labeled = []
  for (const rec of companies) {
    const won = rec.outcomes.some((o) => o.type === 'won' || o.type === 'meeting')
    const lost = rec.outcomes.some((o) => o.type === 'lost')
    if (won || lost) labeled.push({ attrs: rec.attrs || {}, positive: won && !lost ? 1 : 0 })
  }
  const total = labeled.length
  const posOverall = (labeled.reduce((s, l) => s + l.positive, 0) + 1) / (total + 2)
  const buckets = {}
  const drivers = []
  for (const dim of DIMENSIONS) {
    for (const l of labeled) {
      const v = l.attrs[dim.key]
      if (v == null) continue
      const bucketKey = `${dim.key}:${v}`
      const b = (buckets[bucketKey] ||= { pos: 0, n: 0, dim, value: String(v) })
      b.pos += l.positive
      b.n += 1
    }
  }
  for (const [bucketKey, b] of Object.entries(buckets)) {
    if (b.n < MIN_BUCKET) continue
    const rate = (b.pos + 1) / (b.n + 2)
    const lift = rate - posOverall
    drivers.push({
      key: bucketKey,
      label: b.dim.labels[b.value] || b.value,
      lift,
      samples: b.n,
    })
  }
  drivers.sort((a, b) => Math.abs(b.lift) - Math.abs(a.lift))
  return {
    outcomes: total,
    winRate: total ? posOverall : null,
    // How much to trust the shift below, 0-100, purely as a function of how
    // many outcomes have been labelled. This is a shrinkage weight, NOT a
    // measure of model accuracy — it used to be displayed as "model
    // confidence", which claimed something it never computed.
    shrink: Math.min(100, total * 4),
    buckets,
    drivers: drivers.slice(0, 5),
  }
}

// Base score + calibration shift for one company. `calib` is the result of
// calibration() — pass it in so table renders don't re-read the store per row.
export function calibratedScore(company, calib) {
  const attrs = attrsOf(company)
  const base = attrs.base
  if (!calib || !calib.outcomes) return { score: base, base, delta: 0 }
  let shift = 0
  for (const dim of DIMENSIONS) {
    const v = attrs[dim.key]
    if (v == null) continue
    const b = calib.buckets[`${dim.key}:${v}`]
    if (!b || b.n < MIN_BUCKET) continue
    const rate = (b.pos + 1) / (b.n + 2)
    shift += (rate - calib.winRate) * 60
  }
  const scaled = Math.max(-18, Math.min(18, shift)) * (calib.shrink / 100)
  const score = Math.max(0, Math.min(100, Math.round(base + scaled)))
  return { score, base, delta: Math.round(scaled) }
}

// ---------- Workspace summary (Intelligence hub) ----------

function isoWeekStart(d) {
  const date = new Date(d)
  const day = (date.getDay() + 6) % 7 // Monday = 0
  date.setDate(date.getDate() - day)
  date.setHours(0, 0, 0, 0)
  return date
}

export function intelSummary() {
  const store = read()
  const companies = Object.values(store.companies)
  const calib = calibration()
  const enriched = companies.filter((c) => c.enriched).length
  const outcomes = companies.reduce((s, c) => s + c.outcomes.length, 0)
  const won = companies.filter((c) => c.stage === 'won').length

  // Last 8 ISO weeks of activity, oldest first.
  const weeks = []
  const now = isoWeekStart(new Date())
  for (let i = 7; i >= 0; i--) {
    const start = new Date(now)
    start.setDate(start.getDate() - i * 7)
    weeks.push({ start, label: `${start.getMonth() + 1}/${start.getDate()}`, count: 0 })
  }
  for (const entry of store.log) {
    const at = new Date(entry.at)
    for (let i = weeks.length - 1; i >= 0; i--) {
      if (at >= weeks[i].start) {
        weeks[i].count += 1
        break
      }
    }
  }

  // Email coverage among worked accounts vs the ~19% raw-data baseline.
  const withEmail = companies.filter((c) => c.attrs?.email).length
  const emailCoverage = companies.length ? Math.round((withEmail / companies.length) * 100) : 0

  return {
    tracked: companies.length,
    views: companies.reduce((s, c) => s + c.views, 0),
    enriched,
    outcomes,
    won,
    calib,
    weeks,
    emailCoverage,
  }
}

// Priority queue: tracked accounts that haven't resolved (won/lost), ranked by
// calibrated score from their stored attribute snapshots.
export function priorityQueue(limit = 6) {
  const calib = calibration()
  return listIntel()
    .filter((rec) => rec.stage !== 'won' && rec.stage !== 'lost')
    .map((rec) => {
      const base = rec.attrs?.base ?? 40
      let shift = 0
      for (const dim of DIMENSIONS) {
        const v = rec.attrs?.[dim.key]
        if (v == null) continue
        const b = calib.buckets[`${dim.key}:${v}`]
        if (!b || b.n < MIN_BUCKET) continue
        const rate = (b.pos + 1) / (b.n + 2)
        shift += (rate - calib.winRate) * 60
      }
      const scaled = calib.outcomes
        ? Math.max(-18, Math.min(18, shift)) * (calib.shrink / 100)
        : 0
      return { ...rec, score: Math.max(0, Math.min(100, Math.round(base + scaled))) }
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
}
